import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type ICodeEditorProps,
    RadioButton,
    CodeEditor,
    Switch
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "CodeEditor";

const USAGE_CODE = `const [code, setCode] = useState("const total = 0;");

<CodeEditor
    onChangeText={setCode}
    language="typescript"
    value={code}
/>`;

const TOOLBAR_CODE = `<CodeEditor
    toolbarActions={[
        {
            actions: [
                {
                    onPress: handleClear,
                    title: "Clear"
                }
            ],
            title: "Edit"
        }
    ]}
    isShowStatusBar={true}
    isToolbarVisible={true}
    onChangeText={setCode}
    language="typescript"
    value={code}
/>`;

const LAYOUT_CODE = `<CodeEditor
    style={{
        height: 160
    }}
    spreadBehaviour="baseline"
    onChangeText={setCode}
    isScrollable={true}
    value={code}
/>`;

const SAMPLE_CODE = `const greeting = "Hello";

function greet(name: string) {
    return greeting + ", " + name;
}`;

const TYPING_CODE = "function sum(a, b) {}";

const LONG_CODE = `type Item = {
    price: number;
    name: string;
};

const items: Array<Item> = [
    { name: "Notebook", price: 12 },
    { name: "Pencil", price: 2 },
    { name: "Eraser", price: 1 }
];

function getTotal(list: Array<Item>) {
    return list.reduce((total, item) => total + item.price, 0);
}

const total = getTotal(items);`;

const TAB_SIZES = [
    2,
    4
] as const;

const TAB_SIZE_KEYS = {
    2: "codeEditorPage.tabSizeTwo",
    4: "codeEditorPage.tabSizeFour"
} as const;

const SPREAD_BEHAVIOURS = [
    "stretch",
    "baseline",
    "free"
] as const;

const SCROLL_STYLE: ICodeEditorProps["style"] = {
    height: 160
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("codeEditorPage.typingTitle"),
        id: "typing"
    },
    {
        title: t("codeEditorPage.toolbarTitle"),
        id: "toolbar"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("codeEditorPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const CodeEditorPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        code,
        setCode
    ] = useState(SAMPLE_CODE);

    const [
        typingCode,
        setTypingCode
    ] = useState(TYPING_CODE);

    const [
        tabSize,
        setTabSize
    ] = useState<(typeof TAB_SIZES)[number]>(4);

    const [
        toolbarCode,
        setToolbarCode
    ] = useState(SAMPLE_CODE);

    const [
        isLineNumberVisible,
        setIsLineNumberVisible
    ] = useState(true);

    const [
        layoutCode,
        setLayoutCode
    ] = useState(LONG_CODE);

    const [
        spreadBehaviour,
        setSpreadBehaviour
    ] = useState<(typeof SPREAD_BEHAVIOURS)[number]>("stretch");

    const handleClear = () => {
        setToolbarCode("");
    };

    const handleReset = () => {
        setToolbarCode(SAMPLE_CODE);
    };

    const toolbarActions = [
        {
            actions: [
                {
                    title: t("codeEditorPage.actionClear"),
                    onPress: handleClear
                },
                {
                    title: t("codeEditorPage.actionReset"),
                    onPress: handleReset
                }
            ],
            title: t("codeEditorPage.menuEdit")
        }
    ];

    return <Article>
        <ComponentHeader
            lead={t("codeEditorPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("codeEditorPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("codeEditorPage.overviewCaption")}
                isStacked={true}
            >
                <CodeEditor
                    onChangeText={setCode}
                    language="typescript"
                    value={code}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("codeEditorPage.typingTitle")}
            id="typing"
        >
            <Paragraph>
                {t("codeEditorPage.typingText")}
            </Paragraph>
            <Preview
                caption={t("codeEditorPage.typingCaption")}
                isStacked={true}
            >
                <CodeEditor
                    onChangeText={setTypingCode}
                    isShowStatusBar={true}
                    language="typescript"
                    value={typingCode}
                    tabSize={tabSize}
                />
                <div
                    aria-label={t("codeEditorPage.tabSizeLabel")}
                    role="radiogroup"
                >
                    {
                        TAB_SIZES.map((size) => <RadioButton
                            onPress={() => setTabSize(size)}
                            title={t(TAB_SIZE_KEYS[size])}
                            isChecked={tabSize === size}
                            key={size}
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("codeEditorPage.toolbarTitle")}
            id="toolbar"
        >
            <Paragraph>
                {t("codeEditorPage.toolbarText")}
            </Paragraph>
            <Preview
                caption={t("codeEditorPage.toolbarCaption")}
                isStacked={true}
            >
                <CodeEditor
                    isLineNumberVisible={isLineNumberVisible}
                    toolbarActions={toolbarActions}
                    onChangeText={setToolbarCode}
                    isToolbarVisible={true}
                    isShowStatusBar={true}
                    language="typescript"
                    value={toolbarCode}
                />
                <Switch
                    onCheckedChange={setIsLineNumberVisible}
                    title={t("codeEditorPage.lineNumbers")}
                    isChecked={isLineNumberVisible}
                />
            </Preview>
            <CodeBlock
                code={TOOLBAR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("codeEditorPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("codeEditorPage.readOnlyCaption")}
                isStacked={true}
            >
                <CodeEditor
                    isToolbarVisible={true}
                    language="typescript"
                    value={SAMPLE_CODE}
                    editable={false}
                />
            </Preview>
            <Preview
                caption={t("codeEditorPage.disabledCaption")}
                isStacked={true}
            >
                <CodeEditor
                    isToolbarVisible={true}
                    language="typescript"
                    value={SAMPLE_CODE}
                    isDisabled={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("codeEditorPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("codeEditorPage.layoutText")}
            </Paragraph>
            <Preview
                caption={t("codeEditorPage.layoutCaption")}
                isStacked={true}
            >
                <CodeEditor
                    spreadBehaviour={spreadBehaviour}
                    onChangeText={setLayoutCode}
                    language="typescript"
                    style={SCROLL_STYLE}
                    isScrollable={true}
                    value={layoutCode}
                />
                <div
                    aria-label={t("codeEditorPage.spreadLabel")}
                    role="radiogroup"
                >
                    {
                        SPREAD_BEHAVIOURS.map((behaviour) => <RadioButton
                            onPress={() => setSpreadBehaviour(behaviour)}
                            isChecked={spreadBehaviour === behaviour}
                            title={behaviour}
                            key={behaviour}
                        />)
                    }
                </div>
            </Preview>
            <CodeBlock
                code={LAYOUT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("codeEditorPage.a11yInput"),
                    t("codeEditorPage.a11yLabel"),
                    t("codeEditorPage.a11yKeyboard"),
                    t("codeEditorPage.a11yToolbar"),
                    t("codeEditorPage.a11yStates")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("codeEditorPage.propValue"),
                        name: "value",
                        type: "string"
                    },
                    {
                        description: t("codeEditorPage.propOnChangeText"),
                        type: "(text: string) => void",
                        name: "onChangeText"
                    },
                    {
                        description: t("codeEditorPage.propLanguage"),
                        name: "language",
                        type: "string"
                    },
                    {
                        description: t("codeEditorPage.propLanguageLabel"),
                        name: "languageLabel",
                        type: "string"
                    },
                    {
                        description: t("codeEditorPage.propTabSize"),
                        defaultValue: "4",
                        name: "tabSize",
                        type: "number"
                    },
                    {
                        description: t("codeEditorPage.propIsLineNumberVisible"),
                        name: "isLineNumberVisible",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("codeEditorPage.propIsToolbarVisible"),
                        name: "isToolbarVisible",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("codeEditorPage.propToolbarActions"),
                        type: "Array<ISystemToolbarProps>",
                        name: "toolbarActions",
                        defaultValue: "[]"
                    },
                    {
                        description: t("codeEditorPage.propIsShowStatusBar"),
                        name: "isShowStatusBar",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("codeEditorPage.propIsScrollable"),
                        defaultValue: "false",
                        name: "isScrollable",
                        type: "boolean"
                    },
                    {
                        description: t("codeEditorPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"stretch\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("codeEditorPage.propBorderRadius"),
                        name: "borderRadius",
                        type: "number"
                    },
                    {
                        description: t("codeEditorPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("codeEditorPage.propEditable"),
                        name: "editable",
                        type: "boolean"
                    },
                    {
                        description: t("codeEditorPage.propReadOnly"),
                        name: "readOnly",
                        type: "boolean"
                    },
                    {
                        description: t("codeEditorPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("codeEditorPage.propStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        description: t("codeEditorPage.propContainerStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "containerStyle"
                    },
                    {
                        description: t("codeEditorPage.propToolbarContainerStyle"),
                        name: "toolbarContainerStyle",
                        type: "StyleProp<ViewStyle>"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("codeEditorPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("codeEditorPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("codeEditorPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default CodeEditorPage;
